/**
 * CalendarAvailabilityPage
 *
 * CRUD page for managing Calendar Availability Rules per resource.
 * Allows operators to create, edit, delete availability rules with
 * recurring patterns (daily, weekly, custom) and exception dates.
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.6
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Badge, Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AvailabilityRule {
  id: string;
  calendarResourceId: string;
  pattern: 'daily' | 'weekly' | 'custom';
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  validFrom: string | null;
  validUntil: string | null;
  exceptions: string[];
  createdAt: string;
  updatedAt: string;
}

interface CalendarResource {
  id: string;
  name: string;
}

interface FormData {
  pattern: 'daily' | 'weekly' | 'custom';
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  validFrom: string;
  validUntil: string;
  exceptions: string[];
}

const EMPTY_FORM: FormData = {
  pattern: 'daily',
  daysOfWeek: [],
  startTime: '09:00',
  endTime: '17:00',
  validFrom: '',
  validUntil: '',
  exceptions: [],
};

const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_VALUES = [1, 2, 3, 4, 5, 6, 0]; // Monday=1 ... Saturday=6, Sunday=0

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconClock() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PATTERN_LABELS: Record<string, string> = {
  daily: 'Diario',
  weekly: 'Semanal',
  custom: 'Personalizado',
};

function patternBadgeVariant(pattern: string): 'accent' | 'warning' | 'success' {
  switch (pattern) {
    case 'daily': return 'accent';
    case 'weekly': return 'warning';
    case 'custom': return 'success';
    default: return 'accent';
  }
}

function formatDaysOfWeek(days: number[]): string {
  if (!days || days.length === 0) return 'Todos los días';
  if (days.length === 7) return 'Todos los días';
  return days
    .map((d) => {
      const idx = DAY_VALUES.indexOf(d);
      return idx >= 0 ? DAY_LABELS[idx] : '';
    })
    .filter(Boolean)
    .join(', ');
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CalendarAvailabilityPage() {
  const { agentId, resourceId } = useParams<{ agentId: string; resourceId: string }>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [resource, setResource] = useState<CalendarResource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Exception date input
  const [newException, setNewException] = useState('');

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Data Loading ────────────────────────────────────────────────────────

  const loadResource = useCallback(async () => {
    if (!agentId || !resourceId || !token) return;
    try {
      const data = await apiGet<CalendarResource>(
        `/api/agents/${agentId}/calendar/resources/${resourceId}`,
        token
      );
      setResource(data);
    } catch {
      // Non-critical
    }
  }, [agentId, resourceId, token]);

  const loadRules = useCallback(async () => {
    if (!agentId || !resourceId || !token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiGet<AvailabilityRule[]>(
        `/api/agents/${agentId}/calendar/resources/${resourceId}/availability-rules`,
        token
      );
      setRules(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, resourceId, token]);

  useEffect(() => {
    void loadResource();
  }, [loadResource]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  // ─── Form Handlers ───────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setNewException('');
    setModalOpen(true);
  };

  const openEditModal = (rule: AvailabilityRule) => {
    setEditingId(rule.id);
    setForm({
      pattern: rule.pattern,
      daysOfWeek: rule.daysOfWeek || [],
      startTime: rule.startTime,
      endTime: rule.endTime,
      validFrom: rule.validFrom || '',
      validUntil: rule.validUntil || '',
      exceptions: rule.exceptions || [],
    });
    setFormErrors({});
    setNewException('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.startTime) {
      errors.startTime = 'La hora de inicio es requerida';
    }
    if (!form.endTime) {
      errors.endTime = 'La hora de fin es requerida';
    }
    if (form.startTime && form.endTime && form.startTime >= form.endTime) {
      errors.endTime = 'La hora de fin debe ser posterior a la de inicio';
    }
    if ((form.pattern === 'weekly' || form.pattern === 'custom') && form.daysOfWeek.length === 0) {
      errors.daysOfWeek = 'Debe seleccionar al menos un día';
    }
    if (form.validFrom && form.validUntil && form.validFrom > form.validUntil) {
      errors.validUntil = 'La fecha fin debe ser posterior a la fecha inicio';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !agentId || !resourceId || !token) return;
    setSaving(true);
    try {
      const payload = {
        pattern: form.pattern,
        daysOfWeek: form.pattern === 'daily' ? [] : form.daysOfWeek,
        startTime: form.startTime,
        endTime: form.endTime,
        validFrom: form.validFrom || null,
        validUntil: form.validUntil || null,
        exceptions: form.exceptions,
      };

      if (editingId) {
        await apiPut(
          `/api/agents/${agentId}/calendar/availability-rules/${editingId}`,
          payload,
          token,
          { successMessage: 'Regla de disponibilidad actualizada' }
        );
      } else {
        await apiPost(
          `/api/agents/${agentId}/calendar/resources/${resourceId}/availability-rules`,
          payload,
          token,
          { successMessage: 'Regla de disponibilidad creada' }
        );
      }
      closeModal();
      await loadRules();
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId || !agentId || !token) return;
    setDeleting(true);
    try {
      await apiDelete(
        `/api/agents/${agentId}/calendar/availability-rules/${deleteId}`,
        token,
        { successMessage: 'Regla de disponibilidad eliminada' }
      );
      setDeleteId(null);
      await loadRules();
    } catch (e) {
      setError((e as Error).message);
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Exception Helpers ───────────────────────────────────────────────────

  const addException = () => {
    if (!newException) return;
    if (form.exceptions.includes(newException)) return;
    setForm({ ...form, exceptions: [...form.exceptions, newException].sort() });
    setNewException('');
  };

  const removeException = (date: string) => {
    setForm({ ...form, exceptions: form.exceptions.filter((d) => d !== date) });
  };

  // ─── Day Toggle ──────────────────────────────────────────────────────────

  const toggleDay = (day: number) => {
    const current = form.daysOfWeek;
    if (current.includes(day)) {
      setForm({ ...form, daysOfWeek: current.filter((d) => d !== day) });
    } else {
      setForm({ ...form, daysOfWeek: [...current, day] });
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!token) return null;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/app/agents/${agentId}/calendar/resources`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a recursos
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tokens.borderRadius.lg,
                backgroundColor: tokens.colors.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.primary[600],
              }}
            >
              <IconClock />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Reglas de Disponibilidad
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                {resource ? `Recurso: ${resource.name}` : 'Configura cuándo el recurso está disponible'}
              </p>
            </div>
          </div>
          <Button variant="primary" size="md" onClick={openCreateModal}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconPlus /> Crear Regla
            </span>
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}>
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando reglas de disponibilidad...
        </div>
      )}

      {/* Empty state */}
      {!loading && rules.length === 0 && !error && (
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: tokens.colors.neutral[500], padding: 24 }}>
            <IconClock />
            <p style={{ marginTop: 12, marginBottom: 16 }}>No hay reglas de disponibilidad configuradas.</p>
            <Button variant="primary" size="sm" onClick={openCreateModal}>
              Crear primera regla
            </Button>
          </div>
        </Card>
      )}

      {/* Rules List */}
      {!loading && rules.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rules.map((rule) => (
            <Card key={rule.id} variant="outlined" padding="md">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <Badge variant={patternBadgeVariant(rule.pattern)} size="sm">
                      {PATTERN_LABELS[rule.pattern] || rule.pattern}
                    </Badge>
                    <span style={{ fontSize: 15, fontWeight: 600, color: tokens.colors.neutral[900] }}>
                      {rule.startTime} – {rule.endTime}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 13, color: tokens.colors.neutral[500], flexWrap: 'wrap' }}>
                    {rule.pattern !== 'daily' && (
                      <span>Días: {formatDaysOfWeek(rule.daysOfWeek)}</span>
                    )}
                    {rule.validFrom && <span>Desde: {rule.validFrom}</span>}
                    {rule.validUntil && <span>Hasta: {rule.validUntil}</span>}
                    {rule.exceptions && rule.exceptions.length > 0 && (
                      <span>Excepciones: {rule.exceptions.length}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(rule)}>
                    <IconEdit />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(rule.id)}>
                    <IconTrash />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Editar Regla de Disponibilidad' : 'Crear Regla de Disponibilidad'}
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={closeModal} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {formErrors._general && (
            <div style={{ padding: '8px 12px', borderRadius: tokens.borderRadius.md, backgroundColor: tokens.colors.error.light, color: tokens.colors.error.dark, fontSize: 13 }}>
              {formErrors._general}
            </div>
          )}

          {/* Pattern */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
              Patrón de Recurrencia
            </label>
            <select
              value={form.pattern}
              onChange={(e) => {
                const pattern = e.target.value as FormData['pattern'];
                const daysOfWeek = pattern === 'daily' ? [] : pattern === 'weekly' ? [1, 2, 3, 4, 5] : form.daysOfWeek;
                setForm({ ...form, pattern, daysOfWeek });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                borderRadius: tokens.borderRadius.md,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
                outline: 'none',
              }}
            >
              <option value="daily">Diario (todos los días)</option>
              <option value="weekly">Semanal (lunes a viernes)</option>
              <option value="custom">Personalizado (seleccionar días)</option>
            </select>
          </div>

          {/* Days of Week (for weekly and custom) */}
          {(form.pattern === 'weekly' || form.pattern === 'custom') && (
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 8 }}>
                Días de la Semana
              </label>
              {formErrors.daysOfWeek && (
                <div style={{ fontSize: 12, color: tokens.colors.error.dark, marginBottom: 8 }}>{formErrors.daysOfWeek}</div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {DAY_LABELS.map((label, idx) => {
                  const dayValue = DAY_VALUES[idx];
                  const isSelected = form.daysOfWeek.includes(dayValue);
                  return (
                    <button
                      key={dayValue}
                      type="button"
                      onClick={() => toggleDay(dayValue)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: 500,
                        borderRadius: tokens.borderRadius.md,
                        border: `1px solid ${isSelected ? tokens.colors.primary[500] : tokens.colors.neutral[300]}`,
                        backgroundColor: isSelected ? tokens.colors.primary[100] : tokens.colors.neutral[50],
                        color: isSelected ? tokens.colors.primary[700] : tokens.colors.neutral[600],
                        cursor: 'pointer',
                        transition: `all ${tokens.transitions.fast}`,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Time Range */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Input
              label="Hora de Inicio"
              type="time"
              value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              error={formErrors.startTime}
              fullWidth
            />
            <Input
              label="Hora de Fin"
              type="time"
              value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              error={formErrors.endTime}
              fullWidth
            />
          </div>

          {/* Valid From / Until */}
          <div style={{ display: 'flex', gap: 12 }}>
            <Input
              label="Válido Desde (opcional)"
              type="date"
              value={form.validFrom}
              onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
              fullWidth
            />
            <Input
              label="Válido Hasta (opcional)"
              type="date"
              value={form.validUntil}
              onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
              error={formErrors.validUntil}
              fullWidth
            />
          </div>

          {/* Exceptions */}
          <div style={{ border: `1px solid ${tokens.colors.neutral[200]}`, borderRadius: tokens.borderRadius.md, padding: 12 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 8 }}>
              Excepciones (fechas excluidas)
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: form.exceptions.length > 0 ? 12 : 0 }}>
              <input
                type="date"
                value={newException}
                onChange={(e) => setNewException(e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: 13,
                  borderRadius: tokens.borderRadius.sm,
                  border: `1px solid ${tokens.colors.neutral[300]}`,
                  backgroundColor: tokens.colors.neutral[50],
                  color: tokens.colors.neutral[900],
                }}
              />
              <Button variant="ghost" size="sm" onClick={addException} disabled={!newException}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconPlus /> Agregar</span>
              </Button>
            </div>
            {form.exceptions.length === 0 && (
              <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[400] }}>
                Sin excepciones. Agrega fechas en las que esta regla no aplica.
              </p>
            )}
            {form.exceptions.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {form.exceptions.map((date) => (
                  <span
                    key={date}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 8px',
                      fontSize: 12,
                      borderRadius: tokens.borderRadius.sm,
                      backgroundColor: tokens.colors.neutral[100],
                      color: tokens.colors.neutral[700],
                    }}
                  >
                    {date}
                    <button
                      type="button"
                      onClick={() => removeException(date)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: tokens.colors.error.main,
                        padding: 0,
                        lineHeight: 1,
                        fontSize: 14,
                      }}
                      aria-label={`Eliminar excepción ${date}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Eliminar Regla de Disponibilidad"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => setDeleteId(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
          ¿Estás seguro de que deseas eliminar esta regla de disponibilidad? Esta acción no se puede deshacer.
        </p>
      </Modal>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default CalendarAvailabilityPage;
